import _ from "lodash";
import { connect } from "react-redux";
import moment from "moment-timezone";
import React, { Component } from "react";

import { adminRequestsTyped } from "@/api/(requests)/adminTyped";
import { filterPractices, getQueryParams } from "@/utils/filters";
import { isUserAdmin, isUserPCP } from "@/utils/authToken";
import { sharedRequests } from "@/api/(requests)/shared";

import ReportList from "./components/ReportsList";

class Reports extends Component {
  practiceId = getQueryParams("practiceId", this.props.location.search);
  reportType = getQueryParams("reportType", this.props.location.search);
  state = {
    allPractices: [],
    allReportsList: this.props.location.pathname === "/app/reports",
    bhsPracticeOptions: null,
    clearRetryInterval: false,
    filteredReportList: [],
    originalReportList: [],
    patientId: this.props.match.params.patientId,
    patientName: "",
    patientReportsNextToken: null,
    pcpPracticeOptions: null,
    practiceId: this.practiceId ? this.practiceId : "all_practices",
    practiceOptions: [],
    providerReportsNextToken: null,
    reportLookup: {},
    reportType: this.reportType ? this.reportType : "patient",
    retryingReports: false,
    unprocessedLookup: {},
  };

  componentDidMount = async () => {
    if (this.state.patientId) {
      let urlParams = { patientId: this.state.patientId };
      const context = { showLoader: false };
      const patientResponse = await sharedRequests.fetch_patient_details({ context, urlParams });

      this.setState({
        patientName: patientResponse.user.first_name + " " + patientResponse.user.last_name,
      });
    }
    if (isUserAdmin() && this.state.allReportsList) {
      // .js file — typed API call gets no compile-time type checking here
      const practices = await adminRequestsTyped.fetch_all_practices({
        context: { preventAllErrorToast: false, showLoader: false },
      });
      const pcpPracticeOptions = filterPractices(
        practices,
        (practice) => practice["practice_type"] === "Primary Care",
        false,
      );
      const bhsPracticeOptions = filterPractices(
        practices,
        (practice) => practice["practice_type"] === "Behavioral",
        false,
      );

      const practiceOptions =
        this.state.reportType === "patient" ? pcpPracticeOptions : bhsPracticeOptions;

      this.setState({
        bhsPracticeOptions: bhsPracticeOptions,
        pcpPracticeOptions: pcpPracticeOptions,
        practiceOptions: practiceOptions,
        selectedPractice: practiceOptions.find((option) => option.value === this.state.practiceId),
      });
      if (this.state.reportType === "admin" && this.state.practiceId !== "all_practices") {
        await this.fetchReports();
      }
    } else if (this.props.profile) {
      await this.fetchReports();
    }
  };

  async componentDidUpdate(prevProps) {
    if (!prevProps.profile && this.props.profile && !isUserAdmin()) {
      await this.fetchReports();
    }
  }

  componentWillUnmount = () => {
    if (this.state.clearRetryInterval) {
      clearInterval(this.state.clearRetryInterval);
    }
  };
  practiceIdChangeHandler = async (e) => {
    if (!e) {
      return;
    }
    const practiceId = e.value;

    this.state.unprocessedLookup = {};
    this.state.reportLookup = {};
    this.setState({ practiceId: practiceId, selectedPractice: e }, () => this.fetchReports());
  };

  reportTypeChangeHandler = async (e) => {
    let reportType = e.value;

    if (!reportType) {
      reportType = e.target.value;
    }
    const newState = { practiceId: null, reportType: reportType, selectedPractice: null };

    if (reportType === "patient") {
      newState["practiceOptions"] = this.state.pcpPracticeOptions;
    } else {
      newState["practiceOptions"] = this.state.bhsPracticeOptions;
    }
    if (isUserAdmin()) {
      newState["originalReportList"] = [];
      newState["filteredReportList"] = [];
    }

    this.setState(newState, () => {
      if (!isUserAdmin()) {
        this.fetchReports();
      }
    });
  };

  async fetchReports() {
    if (this.state.reportType === "patient" && (isUserPCP() || isUserAdmin())) {
      await this.fetchPatientReportList(this.state.practiceId);
    } else if (isUserAdmin()) {
      await this.fetchAdminReportList();
    }
  }

  fetchAdminReportList = async (nextToken) => {
    try {
      const searchParams = {};

      if (!this.state.practiceId || this.state.practiceId === "all_practices") {
        return;
      }
      searchParams["practiceId"] = this.state.practiceId;
      const data = {
        limit: 10,
        nextToken,
      };
      const response = await sharedRequests.get_billing_reports({ data, searchParams });

      const reports = response["results"];
      const providerReportsNextToken = response["last_evaluated_key"];

      this.separateProcessedAndUnprocessedReports(reports);
      this.sortReports([...reports]);

      this.setState({
        filteredReportList: !this.state.filteredReportList
          ? Object.values(this.state.reportLookup)
          : this.state.filteredReportList.concat(Object.values(this.state.reportLookup)),
        originalReportList: !this.state.originalReportList
          ? Object.values(this.state.reportLookup)
          : this.state.originalReportList.concat(Object.values(this.state.reportLookup)),
        providerReportsNextToken: providerReportsNextToken,
      });
    } catch (err) {
      console.log(err);
    }
  };

  fetchPatientReportList = async (practiceId, nextToken) => {
    try {
      if (this.props.location.pathname === "/app/reports") {
        let practice_id = practiceId;

        const searchParams = {
          practice_id: isUserAdmin() ? practiceId : this.props.profile.practice_id,
        };
        const data = {
          eager_fetch_report_url: false,
          limit: 50,
          next_token: nextToken,
        };
        let response = await sharedRequests.fetch_patients_reports({ data, searchParams });
        let reports = response["items"];
        const last_evaluated_key = response["last_evaluated_key"];

        this.setState({
          filteredReportList: !this.state.filteredReportList
            ? reports
            : this.state.filteredReportList.concat(reports),
          originalReportList: !this.state.originalReportList
            ? reports
            : this.state.originalReportList.concat(reports),
          patientReportsNextToken: last_evaluated_key,
        });
      }
      if (this.props.match.path === "/app/patient/:patientId/report") {
        let urlParams = { patientId: this.state.patientId };
        let context = { showLoader: false };
        let response = await sharedRequests.fetch_patient_report({ context, urlParams });
        let chronologicallySortedReports = this.sortReports([...response]);

        this.setState({
          allReportsList: false,
          filteredReportList: chronologicallySortedReports,
          originalReportList: chronologicallySortedReports,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  retryProcessingReports = () => {
    let report_keys = [];

    Object.values(this.state.unprocessedLookup).forEach((report) => {
      const report_key = {
        id: report["id"],
        timestamp: report["timestamp"],
      };

      if (_.has(report, "entity_type")) {
        report_key["entity_type"] = report["entity_type"];
      }
      if (_.has(report, "practice_id")) {
        report_key["practice_id"] = report["practice_id"];
      }
      report_keys.push(report_key);
    });

    if (report_keys.length > 0) {
      let interval = setInterval(async () => {
        const searchParams = {
          query_type: "report_keys",
        };
        const data = {
          report_keys,
        };
        const response = await sharedRequests.poll_billing_reports({ data, searchParams });

        if (response) {
          response.results.forEach((report) => {
            if (
              _.has(report, "report_url") ||
              _.has(report, "report_part_urls") ||
              report.status === "Failed"
            ) {
              // set the ID of the updated report, update the reportLookUp hash with the new report
              const key = report["id"] + report["timestamp"];
              let updateReportLookup = this.state.reportLookup;

              updateReportLookup[key] = report;
              this.setState({ reportLookup: updateReportLookup });
              // remove that key/val pair from the unprocessed hash
              let updateUnprocessedLookup = _.omit(this.state.unprocessedLookup, key);

              this.setState({ unprocessedLookup: updateUnprocessedLookup });
              this.setState({
                filteredReportList: _.merge(
                  this.state.filteredReportList,
                  Object.values(this.state.reportLookup),
                ),
              });
              if (Object.keys(this.state.unprocessedLookup).length === 0) {
                clearInterval(this.state.clearRetryInterval);
              }
            }
          });
        }
      }, 5000);

      return interval;
    }
  };

  separateProcessedAndUnprocessedReports = (reportsArray) => {
    let unprocessedReports = reportsArray.filter(
      (report) => !report.report_url && report.status === "In-Progress",
    );
    const unprocessedLookup = {};

    unprocessedReports.forEach((report) => {
      const key = report["id"] + report["timestamp"];

      unprocessedLookup[key] = report;
    });

    this.setState({
      unprocessedLookup: _.merge(unprocessedLookup, this.state.unprocessedLookup),
    });

    if (Object.values(this.state.unprocessedLookup).length > 0) {
      this.state.retryingReports = true;
      if (this.state.clearRetryInterval) {
        clearInterval(this.state.clearRetryInterval);
        this.setState({ clearRetryInterval: false });
      }
      let interval = this.retryProcessingReports();

      this.setState({ clearRetryInterval: interval });
    } else {
      if (this.state.retryingReports) {
        clearInterval(this.state.clearRetryInterval);
        this.setState({ clearRetryInterval: false, retryingReports: false });
      }
    }
  };

  sortReports = (reportsArray) => {
    reportsArray = reportsArray.filter((report) => report.status !== "ARCHIVED");

    let sortedReports = reportsArray.sort((a, b) => {
      let d1, d2;

      if (
        Object.prototype.hasOwnProperty.call(a, "timestamp") &&
        Object.prototype.hasOwnProperty.call(b, "timestamp")
      ) {
        d1 = moment.utc(a.timestamp);
        d2 = moment.utc(b.timestamp);
      } else {
        d1 = moment(a.modified_at, "MM/DD/YYYY").unix();
        d2 = moment(b.modified_at, "MM/DD/YYYY").unix();
      }

      return d2 - d1;
    });

    const report_lookup = {};

    sortedReports.forEach((report) => {
      const key = report["id"] + report["timestamp"];

      report_lookup[key] = report;
    });
    this.setState({ reportLookup: report_lookup });

    return sortedReports;
  };

  onSearch = (e) => {
    let searchStr = e.target.value;
    let filteredReports;

    if (this.state.reportType === "patient") {
      filteredReports = [...this.state.originalReportList].filter((report) => {
        let patientFN = report.first_name.toLowerCase();
        let patientLN = report.last_name.toLowerCase();
        let firstNameMatched = patientFN.match(new RegExp("^" + searchStr.toLowerCase()));
        let lastNameMatched = patientLN.match(new RegExp("^" + searchStr.toLowerCase()));
        let fullNameMatched = (patientFN + " " + patientLN).match(
          new RegExp("^" + searchStr.toLowerCase()),
        );

        return firstNameMatched || lastNameMatched || fullNameMatched;
      });
    } else {
      filteredReports = [...this.state.originalReportList].filter((report) => {
        const reportName = report.name.toLowerCase();
        const reportType = report.type.toLowerCase();
        const partsToCheck = reportName.split(" ");

        partsToCheck.unshift(reportName, reportType);

        return partsToCheck.some((part) => part.match(new RegExp("^" + searchStr.toLowerCase())));
      });
    }

    this.setState({
      filteredReportList: filteredReports,
    });
  };

  render() {
    return (
      <ReportList
        allReports={this.state.allReportsList}
        clearRetryInterval={this.state.clearRetryInterval}
        fetchAdminReportList={this.fetchAdminReportList}
        fetchPatientReportList={this.fetchPatientReportList}
        patientId={this.state.patientId}
        patientName={this.state.patientName}
        patientReportsNextToken={this.state.patientReportsNextToken}
        practiceId={this.state.practiceId}
        practiceIdChangeHandler={this.practiceIdChangeHandler}
        practiceOptions={this.state.practiceOptions}
        providerReportsNextToken={this.state.providerReportsNextToken}
        reportList={this.state.filteredReportList}
        reportType={this.state.reportType}
        reportTypeChangeHandler={this.reportTypeChangeHandler}
        searchHandler={this.onSearch}
        selectedPractice={this.state.selectedPractice}
        setPatientReportsNextToken={(patientReportsNextToken) =>
          this.setState({ patientReportsNextToken })
        }
        setProviderReportsNextToken={(providerReportsNextToken) =>
          this.setState({ providerReportsNextToken })
        }
        setReportList={(reportList) => this.setState({ filteredReportList: reportList })}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    configs: state.configs,
    profile: state.auth.profile,
  };
};

export default connect(mapStateToProps, {})(Reports);
